/**
 * Preauthorization Declined Dialog
 * For marking PoC as preauthorization declined (submitted -> denied)
 */

import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Stack,
  alpha,
} from '@mui/material'
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'

interface PreauthDeclinedDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: {
    denial_date: string
    denial_reason: string
  }) => Promise<void>
  loading?: boolean
}

const PreauthDeclinedDialog: React.FC<PreauthDeclinedDialogProps> = ({
  open,
  onClose,
  onSubmit,
  loading = false,
}) => {
  const [denialDate, setDenialDate] = useState<Date | null>(new Date())
  const [denialReason, setDenialReason] = useState('')

  const handleSubmit = async () => {
    if (!denialDate || !denialReason.trim()) {
      return
    }
    // Convert date to ISO string format
    const isoDate = denialDate.toISOString()
    await onSubmit({
      denial_date: isoDate,
      denial_reason: denialReason.trim(),
    })
  }

  const handleClose = () => {
    if (!loading) {
      setDenialDate(new Date())
      setDenialReason('')
      onClose()
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 12px 48px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 2.5,
            pt: 3,
            px: 3,
            background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
            color: 'white',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                backgroundColor: alpha('#fff', 0.2),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CancelIcon sx={{ fontSize: 24 }} />
            </Box>
            <Typography variant="h6" fontWeight={600}>
              Preauthorization Declined
            </Typography>
          </Stack>
          <IconButton
            onClick={handleClose}
            size="small"
            disabled={loading}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: alpha('#fff', 0.1),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ py: '2rem !important', px: '2.5rem !important' }}>
          <Stack spacing={3}>
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                Enter the denial details
              </Typography>
            </Box>
            <DatePicker
              label="Date of Denial"
              value={denialDate}
              onChange={newValue => {
                // Convert PickerValue (Date | Dayjs) to Date
                const dateObj = newValue instanceof Date ? newValue : (newValue as any)?.toDate ? (newValue as any).toDate() : (newValue ? new Date(newValue as any) : null)
                setDenialDate(dateObj)
              }}
              disabled={loading}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'outlined',
                  sx: {
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#f44336',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#f44336',
                        borderWidth: 2,
                      },
                    },
                  },
                },
              }}
            />
            <TextField
              fullWidth
              label="Reason for Denial"
              multiline
              rows={4}
              value={denialReason}
              onChange={e => setDenialReason(e.target.value)}
              placeholder="Enter the reason for denial..."
              disabled={loading}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#f44336',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#f44336',
                    borderWidth: 2,
                  },
                },
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'grey.50',
          }}
        >
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: alpha('#000', 0.04),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !denialDate || !denialReason.trim()}
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CheckCircleIcon />
              )
            }
            sx={{
              backgroundColor: '#f44336',
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
              '&:hover': {
                backgroundColor: '#d32f2f',
                boxShadow: '0 6px 16px rgba(244, 67, 54, 0.4)',
              },
              '&:disabled': {
                backgroundColor: alpha('#f44336', 0.5),
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  )
}

export default PreauthDeclinedDialog
